/**
 * A text file in the preview (Arthur, 21 October, after Documents went live): a .txt is shown as its text, read from
 * the same audited open as any document, and only when the server served it as text/plain (the server decides that
 * from the bytes: declared text/plain, no NUL, not opening like markup; api/src/upload-kind.ts). Above TEXT_CAP_BYTES
 * only the start is shown, and the panel says so. Tested in web/test/text-preview.test.ts.
 */
export type Viewer = 'pdf' | 'image' | 'video' | 'text' | null;

/** The most of a text file the preview reads: 1 MB. */
export const TEXT_CAP_BYTES = 1024 * 1024;

/** Text files the preview tries, by name: the served type still has the last word. */
const TEXT_NAMES = new Set(['txt']);

/** How the preview tries a file: what the list said, else text for a .txt. */
export function viewerFor(name: string, given: Viewer): Viewer {
  if (given !== null) return given;
  const ext = /\.([^.]+)$/.exec(name)?.[1]?.toLowerCase() ?? '';
  return TEXT_NAMES.has(ext) ? 'text' : null;
}

/** How the preview tries an upload, by its name (P-20: shared by Documents and the record's "View document"). */
export function uploadViewer(name: string): Viewer {
  const ext = /\.([^.]+)$/.exec(name)?.[1]?.toLowerCase() ?? '';
  const viewer: Viewer = ext === 'pdf' ? 'pdf' : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? 'image'
    : ['mov', 'mp4', 'm4v', 'webm'].includes(ext) ? 'video' : null;
  return viewerFor(name, viewer);
}

/** The served type, without its parameters ("text/plain; charset=utf-8" is text/plain). */
export const TEXT_TYPE = 'text/plain';

/**
 * The text to show: the whole file, or its first TEXT_CAP_BYTES with `cut` set. A character the cap splits in two
 * decodes as a replacement character at the very end, and is dropped.
 */
export async function readTextPreview(blob: Blob, cap = TEXT_CAP_BYTES): Promise<{ text: string; cut: boolean; bytes: number }> {
  const cut = blob.size > cap;
  let text = await (cut ? blob.slice(0, cap) : blob).text();
  if (cut) text = text.replace(/\uFFFD+$/, '');
  return { text, cut, bytes: blob.size };
}
